import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { lazy, Suspense, useEffect } from "react";
import { Route, Switch, Redirect, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { VersionWatcher } from "./components/VersionWatcher";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import Layout from "./components/Layout";
import { AuthAnalyticsTracker } from "./components/AuthAnalyticsTracker";
import { PurchaseAnalytics } from "./components/PurchaseAnalytics";
import { ScrollToTop } from "./components/ScrollToTop";
import { FloatingNavButtons } from "./components/FloatingNavButtons";
import { InfolinksScript } from "./components/InfolinksScript";
import { ConsentBanner } from "./components/ConsentBanner";
import { withLocaleGuard } from "./components/withLocaleGuard";
import { isBookWritingToolboxDomain } from "./lib/subdomainBranding";

// Route-level code splitting: previously every page below was imported
// eagerly here, which meant ToolPage/ProfilePage/etc. all shipped inside
// the same main JS bundle loaded on EVERY visit -- including a first-time
// mobile visitor landing on the homepage who doesn't need ProfilePage's
// Team Pro/Stripe UI at all yet. PageSpeed's "reduce unused JavaScript"
// flag was pointing directly at this (ToolPage.tsx and ProfilePage.tsx
// named inside the main chunk). Individual tools were already correctly
// split via React.lazy() -- this extends the same pattern one level up,
// to the page shells themselves. Home stays eager since it's the most
// common landing page and we want it painting immediately with no
// Suspense flash.
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const AllToolsPage = lazy(() => import("./pages/AllToolsPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const NewsletterPage = lazy(() => import("./pages/NewsletterPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const ToolPage = lazy(() => import("./pages/ToolPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const AdminUsersPage = lazy(() => import("./pages/AdminUsersPage"));
const AdminSearchInsightsPage = lazy(() => import("./pages/AdminSearchInsightsPage"));
const AdminAccessCodesPage = lazy(() => import("./pages/AdminAccessCodesPage"));
const AdminHomePage = lazy(() => import("./pages/AdminHomePage"));
const AdminFunnelPage = lazy(() => import("./pages/AdminFunnelPage"));
const AdminVisitorsPage = lazy(() => import("./pages/AdminVisitorsPage"));
const AdminOrganizationsPage = lazy(() => import("./pages/AdminOrganizationsPage"));
const OrganizationPage = lazy(() => import("./pages/OrganizationPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const BlogListPage = lazy(() => import("./pages/BlogListPage"));
const MyToolkitPage = lazy(() => import("./pages/MyToolkitPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const SolutionsHub = lazy(() => import("./pages/SolutionsHub"));
const PremiumAppsPage = lazy(() => import("./pages/PremiumAppsPage"));
const ProfessionalToolsPage = lazy(() => import("./pages/ProfessionalToolsPage"));
const WorkflowProductsPage = lazy(() => import("./pages/WorkflowProductsPage"));
const BundlesHubPage = lazy(() => import("./pages/BundlesHubPage"));
const BundlePage = lazy(() => import("./pages/BundlePage"));
const CustomSoftwarePage = lazy(() => import("./pages/CustomSoftwarePage"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const SolutionPage = lazy(() => import("./pages/SolutionPage"));
const SpecPage = lazy(() => import("./pages/SpecsPage"));
const SpecsHubPage = lazy(() => import("./pages/SpecsPage").then((m) => ({ default: m.SpecsHubPage })));
const CorporateSafeTools = lazy(() => import("./pages/CorporateSafeTools"));
const PipelineRunnerPage = lazy(() => import("./pages/PipelineRunnerPage"));
const DevelopersPage = lazy(() => import("./pages/DevelopersPage"));
const ApiUseCasePage = lazy(() => import("./pages/ApiUseCasePage"));
const DoorCheckApp = lazy(() => import("./pages/apps/DoorCheckApp"));
const BoothLeadsApp = lazy(() => import("./pages/apps/BoothLeadsApp"));
const GuestApp = lazy(() => import("./pages/apps/GuestApp"));

const LocaleHome = withLocaleGuard(Home);
const LocaleToolPage = withLocaleGuard(ToolPage);
const LocaleContact = withLocaleGuard(Contact);
const LocaleCategoryPage = withLocaleGuard(CategoryPage);
const LocaleAllToolsPage = withLocaleGuard(AllToolsPage);
const LocaleSolutionsHub = withLocaleGuard(SolutionsHub);
const LocaleSolutionPage = withLocaleGuard(SolutionPage);
const LocaleBlogListPage = withLocaleGuard(BlogListPage);
const LocaleBlogPostPage = withLocaleGuard(BlogPostPage);
const LocaleFaqPage = withLocaleGuard(FaqPage);
const LocaleCorporateSafeTools = withLocaleGuard(CorporateSafeTools);

// Minimal, theme-neutral fallback -- shown only for the brief moment a
// lazy chunk is fetching. Pre-rendered static HTML (already in the build
// pipeline) means real visitors/crawlers see full content immediately on
// first paint; this fallback only ever appears on client-side
// navigations between routes, not on initial page load.
function RouteFallback() {
  // Tall enough that the footer stays below the fold until the page chunk arrives; a short placeholder let the
  // footer sit in view and jump down when the page rendered (the layout shift the QA report measured).
  return <div className="min-h-screen" aria-hidden="true" />;
}

function Router() {
  const [location] = useLocation();
  // The installable event apps are full-screen: no site header, footer or pop-ups, and they must load with no signal.
  if (location.startsWith("/apps/")) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Switch>
          <Route path="/apps/door/:token?" component={DoorCheckApp} />
          <Route path="/apps/booth/:token?" component={BoothLeadsApp} />
          <Route path="/apps/guest/:token?" component={GuestApp} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    );
  }
  return (
    <Layout>
      <Suspense fallback={<RouteFallback />}>
        <Switch>
          {/* Book Writing Toolbox's own subdomain -- its root path goes
              straight to the tool itself rather than freedailypro.com's
              own 200-tool homepage, so visiting the subdomain feels
              like landing on a dedicated product, not a generic
              multi-tool site. See subdomainBranding.ts for the full
              reasoning; this is the one place that check happens for
              routing. */}
          <Route path="/" component={() => (isBookWritingToolboxDomain() ? <Redirect to="/tool/book-writing-toolbox" /> : <Home />)} />
          <Route path="/tools" component={AllToolsPage} />
          <Route path="/pricing" component={PricingPage} />
          <Route path="/newsletter" component={NewsletterPage} />
          <Route path="/careers" component={CareersPage} />
          <Route path="/category/:id" component={CategoryPage} />
          {/* "Images to PDF" was a separately-catalogued entry that
              shared its exact component with "Image to PDF" -- the
              same identical page under two names/URLs was producing a
              real duplicate-content signal in Search Console (Google
              overriding the site's own declared canonical on ~102
              pages). Merged into one tool; this route preserves the
              old URL as a real redirect rather than a dead 404, so
              any existing indexing/backlinks consolidate onto the
              surviving page instead of being lost. Must come before
              the generic /tool/:id route below -- wouter's Switch
              matches in order, first match wins. */}
          <Route path="/tool/images-to-pdf" component={() => <Redirect to="/tool/image-to-pdf" />} />
          {/* Same slug-vs-H1-name mismatch pattern -- the page's own
              visible title is "Convert Formats" but its real slug is
              convert-image, so someone typing the intuitive URL based
              on what they saw on the page would hit a dead 404 instead
              of the real tool. A real redirect, not just a fixed
              broken link, so any existing backlinks or bookmarks using
              this URL still land somewhere real. */}
          <Route path="/tool/convert-formats" component={() => <Redirect to="/tool/convert-image" />} />
          {/* Renamed from "Book Maker" to "Book Writing Toolbox" --
              "Book Maker" is an established gambling/sports-betting
              term, which was producing genuinely confused search
              intent for anyone searching the tool's own name. A real
              redirect, not a dead 404, so existing indexing,
              backlinks, and bookmarks using the old URL still land on
              the real tool. */}
          <Route path="/tool/book-maker" component={() => <Redirect to="/tool/book-writing-toolbox" />} />
          <Route path="/tool/retirement-calculator" component={() => <Redirect to="/tool/401k-calculator" />} />
          <Route path="/tool/zip" component={() => <Redirect to="/tool/zip-unzip" />} />
          {/* The five event suites became one Event Suite product. */}
          {["event-planner-suite", "corporate-event-suite", "wedding-celebration-suite", "conference-suite", "exhibition-suite"].map((old) => (
            <Route key={old} path={`/tool/${old}`} component={() => <Redirect to="/tool/event-suite" />} />
          ))}
          {/* /tool/unit-converter/<pair> and any other /tool/<id>/<x>: ToolPage rejects what is not a real landing page. */}
          <Route path="/tool/:id/:pair" component={ToolPage} />
          <Route path="/tool/:id" component={ToolPage} />
          <Route path="/profile" component={ProfilePage} />
          <Route path="/account" component={() => <Redirect to="/profile" />} />
          <Route path="/workflows" component={() => <Redirect to="/workflow-products" />} />
          <Route path="/admin" component={AdminHomePage} />
          <Route path="/admin/organizations" component={AdminOrganizationsPage} />
          <Route path="/organization" component={OrganizationPage} />
          <Route path="/admin/users" component={AdminUsersPage} />
          <Route path="/admin/funnel" component={AdminFunnelPage} />
          <Route path="/admin/visitors" component={AdminVisitorsPage} />
          <Route path="/admin/search-insights" component={AdminSearchInsightsPage} />
          <Route path="/admin/access-codes" component={AdminAccessCodesPage} />
          <Route path="/developers" component={DevelopersPage} />
          <Route path="/developers/use-cases/:slug" component={ApiUseCasePage} />
          <Route path="/privacy" component={PrivacyPolicy} />
          <Route path="/terms" component={TermsOfService} />
          <Route path="/about" component={About} />
          <Route path="/contact" component={Contact} />
          <Route path="/blog" component={BlogListPage} />
          <Route path="/my-toolkit" component={MyToolkitPage} />
          <Route path="/blog/page/:n" component={BlogListPage} />
          <Route path="/blog/topic/:topic" component={BlogListPage} />
          <Route path="/blog/:slug" component={BlogPostPage} />
          <Route path="/solutions" component={SolutionsHub} />
          <Route path="/premium-apps" component={PremiumAppsPage} />
          <Route path="/professional-tools" component={ProfessionalToolsPage} />
          <Route path="/workflow-products" component={WorkflowProductsPage} />
          <Route path="/custom-software" component={CustomSoftwarePage} />
          <Route path="/faq" component={FaqPage} />
          <Route path="/corporate-safe-tools" component={CorporateSafeTools} />
          <Route path="/bundles" component={BundlesHubPage} />
          <Route path="/bundle/:id" component={BundlePage} />
          <Route path="/pipeline" component={PipelineRunnerPage} />
          <Route path="/solutions/:id" component={SolutionPage} />
          <Route path="/specs" component={SpecsHubPage} />
          <Route path="/specs/:slug" component={SpecPage} />
          {/* Locale-prefixed routes -- scoped tonight to Home, tool pages, and
              Contact per the agreed priority. Same components; useLocale()
              reads the locale back out of the URL inside them. Wouter's
              matcher (regexparam) doesn't support constrained params like
              :locale(es|pt|fr), so the actual locale value is validated
              inside LocaleRouteGuard rather than at the route-match level. */}
          <Route path="/:locale" component={LocaleHome} />
          <Route path="/:locale/tool/:id" component={LocaleToolPage} />
          <Route path="/:locale/contact" component={LocaleContact} />
          <Route path="/:locale/category/:id" component={LocaleCategoryPage} />
          <Route path="/:locale/tools" component={LocaleAllToolsPage} />
          <Route path="/:locale/solutions" component={LocaleSolutionsHub} />
          <Route path="/:locale/solutions/:id" component={LocaleSolutionPage} />
          <Route path="/:locale/blog" component={LocaleBlogListPage} />
          <Route path="/:locale/blog/:slug" component={LocaleBlogPostPage} />
          <Route path="/:locale/faq" component={LocaleFaqPage} />
          <Route path="/:locale/corporate-safe-tools" component={LocaleCorporateSafeTools} />
          <Route path="/404" component={NotFound} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </Layout>
  );
}

function App() {
  useEffect(() => {
    // Warms the search module's cache during idle time after first
    // paint, so the actual first keystroke into any of the three
    // search surfaces (hero, header, command palette) doesn't need a
    // cold import -- reduces, though doesn't eliminate, how often a
    // stale-chunk failure could ever be hit mid-typing in the first
    // place (the real fix for when it does happen either way is each
    // search handler's own .catch(), not this). Silently ignored if it
    // fails -- this is just a warm-up, not something the user is
    // waiting on, so there's nothing useful to show them if it doesn't
    // pan out.
    const idle = (cb: () => void) =>
      "requestIdleCallback" in window ? window.requestIdleCallback(cb) : setTimeout(cb, 1000);
    idle(() => {
      import("@/lib/knowledgeBaseSearch").catch(() => {
        /* silent -- this is just a prefetch, not a user-visible action */
      });
    });
  }, []);

  return (
    <ErrorBoundary>
      <VersionWatcher />
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          {/* Lifted above the floating Feedback button (bottom-right): QA measured the
              default position overlapping it, and a toast's action button (the damaged-file
              "Repair PDF" one) landing directly beside it. */}
          <Toaster offset={{ right: 24, bottom: 132 }} mobileOffset={{ right: 16, left: 16, bottom: 132 }} />
          <AuthAnalyticsTracker />
          <PurchaseAnalytics />
          <ScrollToTop />
          <FloatingNavButtons />
          <ConsentBanner />
          <InfolinksScript />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
